import React, { useState, useRef, useEffect } from 'react'
import { Box, Typography, IconButton, Stack, Fade, Button } from '@mui/material'
import { Close, WhatsApp } from '@mui/icons-material'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import candidates from '../data/candidates.json'

const ChatBot = () => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [isTyping, setIsTyping] = useState(false)
  const [greeted, setGreeted] = useState(false)
  const endRef = useRef(null)

  const topics = t('chatbot.topics', { returnObjects: true }) || []
  const topicsHeader = t('chatbot.topicsHeader')
  const topicsAgainHeader = t('chatbot.topicsAgainHeader')

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isTyping])

  useEffect(() => {
    if (!open) return
    const id = setTimeout(() => {
      endRef.current?.scrollIntoView({ behavior: 'auto' })
    }, 50)
    return () => clearTimeout(id)
  }, [open])

  useEffect(() => {
    if (open && !greeted) {
      setIsTyping(true)
      const timer = setTimeout(() => {
        setIsTyping(false)
        setMessages([
          { from: 'bot', text: t('chatbot.greeting'), showWA: false },
          { from: 'topics', header: topicsHeader },
        ])
        setGreeted(true)
      }, 900)
      return () => clearTimeout(timer)
    }
  }, [open, greeted, t, topicsHeader])

  const handleTopicClick = (topic) => {
    setMessages(prev => [...prev, { from: 'user', text: topic.label }])
    setIsTyping(true)
    setTimeout(() => {
      setIsTyping(false)
      const responseText = t(`chatbot.responses.${topic.key}`, { jumlah: candidates.length })
      setMessages(prev => [
        ...prev,
        { from: 'bot', text: responseText, showWA: !!topic.showWA, linkTo: topic.linkTo || null },
        { from: 'topics', header: topicsAgainHeader },
      ])
    }, 600)
  }

  const resetChat = () => {
    setIsTyping(true)
    setMessages([])
    setTimeout(() => {
      setIsTyping(false)
      setMessages([
        { from: 'bot', text: t('chatbot.greeting'), showWA: false },
        { from: 'topics', header: topicsHeader },
      ])
    }, 500)
  }

  const openWA = (msg) => {
    const text = msg || 'Halo CV Mulia Utama Mandiri, saya ingin bertanya lebih lanjut. Terima kasih.'
    window.open(`https://wa.me/${t('footer.number')}?text=${encodeURIComponent(text)}`, '_blank')
  }

  return (
    <Box sx={{ position: 'fixed', bottom: 28, right: 28, zIndex: 1400 }}>
      {/* Chat window */}
      <Fade in={open} unmountOnExit>
        <Box sx={{
          position: 'absolute', bottom: 70, right: 0,
          width: { xs: 'min(calc(100vw - 48px), 340px)', sm: 340 },
          height: 500,
          borderRadius: '20px', overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2), 0 4px 16px rgba(0,0,0,0.1)',
          display: 'flex', flexDirection: 'column',
        }}>

          {/* Header */}
          <Box sx={{
            background: 'linear-gradient(135deg, #001a40 0%, #002855 100%)',
            px: 2.5, py: 2,
            display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0,
          }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: '12px',
              background: 'rgba(249,115,22,0.15)',
              border: '1.5px solid rgba(249,115,22,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <SmartToyOutlinedIcon sx={{ color: '#f97316', fontSize: '1.25rem' }} />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.87rem', lineHeight: 1.2 }}>
                {t('chatbot.title')}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4ade80' }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.67rem' }}>
                  {t('chatbot.online')}
                </Typography>
              </Stack>
            </Box>
            <IconButton onClick={resetChat} size="small" title="Reset"
              sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}>
              <RestartAltIcon sx={{ fontSize: 17 }} />
            </IconButton>
            <IconButton onClick={() => setOpen(false)} size="small"
              sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}>
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box sx={{
            flexGrow: 1, overflowY: 'auto', px: 2, py: 2,
            bgcolor: '#f1f5f9',
            display: 'flex', flexDirection: 'column', gap: 1.5,
            '&::-webkit-scrollbar': { width: 3 },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 2 },
          }}>
            {messages.map((msg, i) => {
              if (msg.from === 'topics') {
                return (
                  <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 0.85, alignItems: 'flex-start' }}>
                    <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, mb: 0.3 }}>
                      {msg.header || topicsHeader}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                      {topics.map((topic, j) => (
                        <Box
                          key={j}
                          onClick={() => handleTopicClick(topic)}
                          sx={{
                            px: 1.5, py: 0.7,
                            bgcolor: '#fff',
                            border: '1.5px solid #e2e8f0',
                            borderRadius: '14px',
                            cursor: 'pointer',
                            fontSize: '0.76rem', fontWeight: 600, color: '#334155',
                            transition: 'all 0.15s ease',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            '&:hover': {
                              bgcolor: '#002855',
                              color: '#fff',
                              borderColor: '#002855',
                            },
                          }}
                        >
                          {topic.label}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )
              }

              return (
                <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.from === 'user' ? 'flex-end' : 'flex-start', gap: 0.75 }}>
                  <Box sx={{
                    maxWidth: '82%', px: 2, py: 1.25,
                    borderRadius: msg.from === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    bgcolor: msg.from === 'user' ? '#002855' : '#fff',
                    color: msg.from === 'user' ? '#fff' : '#1e293b',
                    fontSize: '0.83rem', lineHeight: 1.65,
                    whiteSpace: 'pre-line',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
                  }}>
                    {msg.text}
                  </Box>
                  {msg.linkTo && (
                    <Button size="small"
                      component={Link}
                      to={msg.linkTo}
                      onClick={() => setOpen(false)}
                      endIcon={<OpenInNewIcon sx={{ fontSize: '0.85rem !important' }} />}
                      sx={{
                        bgcolor: '#002855', color: '#fff',
                        borderRadius: '20px', px: 1.75, py: 0.4,
                        fontSize: '0.73rem', fontWeight: 700, textTransform: 'none',
                        boxShadow: '0 2px 8px rgba(0,40,85,0.3)',
                        '&:hover': { bgcolor: '#0056B3' },
                      }}
                    >
                      {t('chatbot.viewCandidatesButton')}
                    </Button>
                  )}
                  {msg.showWA && (
                    <Button size="small"
                      startIcon={<WhatsApp sx={{ fontSize: '0.85rem !important' }} />}
                      onClick={() => openWA()}
                      sx={{
                        bgcolor: '#25D366', color: '#fff',
                        borderRadius: '20px', px: 1.75, py: 0.4,
                        fontSize: '0.73rem', fontWeight: 700, textTransform: 'none',
                        boxShadow: '0 2px 8px rgba(37,211,102,0.3)',
                        '&:hover': { bgcolor: '#1da851' },
                      }}
                    >
                      {t('chatbot.whatsappButton')}
                    </Button>
                  )}
                </Box>
              )
            })}

            {/* Typing indicator */}
            {isTyping && (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.6,
                px: 2, py: 1.25, bgcolor: '#fff',
                borderRadius: '16px 16px 16px 4px', width: 'fit-content',
                boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
              }}>
                {[0, 1, 2].map(i => (
                  <Box key={i} sx={{
                    width: 7, height: 7, borderRadius: '50%', bgcolor: '#94a3b8',
                    animation: 'botBounce 1.2s infinite',
                    animationDelay: `${i * 0.2}s`,
                    '@keyframes botBounce': {
                      '0%, 60%, 100%': { transform: 'translateY(0)' },
                      '30%': { transform: 'translateY(-5px)' },
                    },
                  }} />
                ))}
              </Box>
            )}

            <div ref={endRef} />
          </Box>
        </Box>
      </Fade>

      {/* Floating button */}
      <Box
        onClick={() => setOpen(p => !p)}
        sx={{
          width: 56, height: 56, borderRadius: '50%',
          background: open ? '#475569' : 'linear-gradient(135deg, #002855 0%, #0056B3 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: open ? '0 4px 16px rgba(0,0,0,0.2)' : '0 6px 24px rgba(0,40,85,0.4)',
          transition: 'all 0.25s ease',
          '&:hover': { transform: 'scale(1.07)' },
          position: 'relative',
        }}
      >
        <Fade in={!open} timeout={200}>
          <SmartToyOutlinedIcon sx={{ color: '#fff', fontSize: 26, position: 'absolute' }} />
        </Fade>
        <Fade in={open} timeout={200}>
          <Close sx={{ color: '#fff', fontSize: 22, position: 'absolute' }} />
        </Fade>
      </Box>
    </Box>
  )
}

export default ChatBot
